import type {
  TextSection,
  TextSectionBoundary,
  TextSectionType,
  TextStructureCommand,
  TextStructureLayer,
} from '../types/readerTextStructureTypes'

export const DEFAULT_TEXT_SECTION_TYPES: readonly Pick<TextSectionType, 'id' | 'label' | 'abbreviation'>[] = [
  { id: 'scene', label: 'Scene', abbreviation: 'scene' },
  { id: 'bab', label: 'Bāb', abbreviation: 'bāb' },
]

export const getTextStructureLayerId = (chapterId: string, sourceWitnessId: string): string => (
  `${chapterId}::${sourceWitnessId}`
)

export const createTextStructureLayer = ({
  chapterId,
  sourceWitnessId,
  now = new Date().toISOString(),
}: {
  chapterId: string
  sourceWitnessId: string
  now?: string
}): TextStructureLayer => ({
  id: getTextStructureLayerId(chapterId, sourceWitnessId),
  chapterId,
  sourceWitnessId,
  version: 1,
  types: DEFAULT_TEXT_SECTION_TYPES.map(sectionType => ({ ...sectionType, createdAt: now })),
  sections: [],
  createdAt: now,
  updatedAt: now,
})

const compareBoundaries = (
  left: TextSectionBoundary,
  right: TextSectionBoundary,
  rowOrderById: ReadonlyMap<string, number>,
): number => {
  const leftRow = rowOrderById.get(left.rowId)
  const rightRow = rowOrderById.get(right.rowId)
  if (leftRow === undefined || rightRow === undefined) {
    throw new Error('A section boundary refers to a unit outside the current text')
  }
  return leftRow - rightRow || left.offset - right.offset
}

const rangesOverlap = (
  left: TextSection,
  right: TextSection,
  rowOrderById: ReadonlyMap<string, number>,
): boolean => (
  compareBoundaries(left.start, right.end, rowOrderById) < 0 &&
  compareBoundaries(right.start, left.end, rowOrderById) < 0
)

const rangesCross = (
  left: TextSection,
  right: TextSection,
  rowOrderById: ReadonlyMap<string, number>,
): boolean => {
  const leftStartsFirst = compareBoundaries(left.start, right.start, rowOrderById) < 0
  const rightStartsInsideLeft = compareBoundaries(right.start, left.end, rowOrderById) < 0
  const leftEndsInsideRight = compareBoundaries(left.end, right.end, rowOrderById) < 0
  const rightStartsFirst = compareBoundaries(right.start, left.start, rowOrderById) < 0
  const leftStartsInsideRight = compareBoundaries(left.start, right.end, rowOrderById) < 0
  const rightEndsInsideLeft = compareBoundaries(right.end, left.end, rowOrderById) < 0
  return (
    leftStartsFirst && rightStartsInsideLeft && leftEndsInsideRight
  ) || (
    rightStartsFirst && leftStartsInsideRight && rightEndsInsideLeft
  )
}

export const validateTextSection = ({
  layer,
  section,
  rowOrderById,
  replacingSectionId = '',
}: {
  layer: TextStructureLayer
  section: TextSection
  rowOrderById: ReadonlyMap<string, number>
  replacingSectionId?: string
}): void => {
  if (!layer.types.some(sectionType => sectionType.id === section.typeId)) {
    throw new Error('Choose a section type defined for this text')
  }
  if (section.start.witnessId !== layer.sourceWitnessId || section.end.witnessId !== layer.sourceWitnessId) {
    throw new Error('Section boundaries must belong to the current source text')
  }
  if (compareBoundaries(section.start, section.end, rowOrderById) >= 0) {
    throw new Error('A section must end after it begins')
  }

  layer.sections
    .filter(candidate => candidate.id !== replacingSectionId)
    .forEach(candidate => {
      if (rangesCross(section, candidate, rowOrderById)) {
        throw new Error('Sections may nest, but their boundaries may not cross')
      }
      if (candidate.typeId === section.typeId && rangesOverlap(section, candidate, rowOrderById)) {
        throw new Error(`Two ${section.typeId} sections may be adjacent, but may not overlap`)
      }
    })
}

export const applyTextStructureCommand = ({
  layer,
  command,
  rowOrderById,
  now = new Date().toISOString(),
}: {
  layer: TextStructureLayer
  command: TextStructureCommand
  rowOrderById: ReadonlyMap<string, number>
  now?: string
}): TextStructureLayer => {
  let types = layer.types
  let sections = layer.sections

  if (command.op === 'add-type') {
    const id = command.sectionType.id.trim()
    const label = command.sectionType.label.trim()
    const abbreviation = command.sectionType.abbreviation.trim()
    if (!id || !label || !abbreviation) {
      throw new Error('A custom section type needs an id, label, and abbreviation')
    }
    if (types.some(sectionType => sectionType.id === id)) {
      throw new Error(`The section type ${id} already exists in this text`)
    }
    types = [...types, { ...command.sectionType, id, label, abbreviation }]
  } else if (command.op === 'add-section') {
    if (sections.some(section => section.id === command.section.id)) {
      throw new Error(`The section ${command.section.id} already exists`)
    }
    validateTextSection({ layer, section: command.section, rowOrderById })
    sections = [...sections, command.section]
  } else if (command.op === 'update-section') {
    const existing = sections.find(section => section.id === command.sectionId)
    if (!existing) {
      throw new Error(`The section ${command.sectionId} does not exist`)
    }
    const updated: TextSection = {
      ...existing,
      ...command.updates,
      title: command.updates.title?.trim() || undefined,
      updatedAt: now,
    }
    validateTextSection({
      layer,
      section: updated,
      rowOrderById,
      replacingSectionId: command.sectionId,
    })
    sections = sections.map(section => section.id === command.sectionId ? updated : section)
  } else {
    sections = sections.filter(section => section.id !== command.sectionId)
  }

  return {
    ...layer,
    version: layer.version + 1,
    types,
    sections,
    updatedAt: now,
  }
}
